import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
} from "reactstrap";
import {
  FaFilter,
  FaPlus,
  FaSearch,
  FaSortAmountDown,
  FaSortAmountDownAlt,
  FaSpinner,
  FaTimes,
} from "react-icons/fa";
import {
  DateBar,
  InputBar,
  MultiSelectDropdown,
  SelectDropdown,
} from "./FilterControls";
import { STATUS_META } from "./StatusBadge";

const statusOptions = [
  "DRAFT",
  "SUBMITTED",
  "KYC_PENDING",
  "KYC_COMPLETE",
  "IN_REVIEW",
  "NEEDS_CLARIFICATION",
  "APPROVED",
  "ACCOUNT_PROVISIONED",
  "REJECTED",
  "EXPIRED",
  "WITHDRAWN",
].map((key) => ({ value: key, label: STATUS_META[key].label }));

const kycStatusOptions = [
  "NOT_GENERATED",
  "GENERATED",
  "SENT",
  "OPENED",
  "IN_PROGRESS",
  "COMPLETED",
  "EXPIRED",
  "FAILED",
].map((key) => ({ value: key, label: STATUS_META[key].label }));

// Individual Plus is formType-based; the three business families are the numeric
// `type` sub-codes of formType BUSINESS. Only Business Plus is live for now —
// the rest are listed for forward compatibility.
const applicationTypeOptions = [
  { value: "INDIVIDUAL_PLUS", label: "Individual Plus" },
  { value: "1", label: "Business Plus" },
  { value: "2", label: "Business Prime" },
  { value: "3", label: "Business Prime Upgrade" },
];

const viewOptions = [
  { value: "all", label: "All applications" },
  { value: "drafts", label: "Drafts" },
  { value: "needs_action", label: "Needs Action" },
  { value: "missing_info", label: "Additional Info" },
  { value: "kyc_failed", label: "Verification Issues" },
  { value: "kyc_pending", label: "KYC Pending" },
  { value: "in_review", label: "In Review" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
];

const sortFieldOptions = [
  { value: "updatedAt", label: "Updated" },
  { value: "createdAt", label: "Created" },
  { value: "brokerReferenceId", label: "Reference" },
  { value: "customerName", label: "Client" },
];

const EMPTY_OPTIONAL_KEYS = [];

const toValueArray = (value) => String(value || "").split(",").filter(Boolean);
const toCsv = (values) => (values || []).join(",");

// Status is a fixed, non-removable control. Everything else lives behind "Add filter".
const optionalFilterDefinitions = [
  {
    key: "view",
    label: "View",
    isActive: (filters) => Boolean(filters.view && filters.view !== "all"),
    clearKeys: ["view"],
  },
  {
    key: "kycStatus",
    label: "KYC status",
    isActive: (filters) => Boolean(filters.kycStatus),
  },
  {
    key: "type",
    label: "Application type",
    isActive: (filters) => Boolean(filters.type),
  },
  {
    key: "createdRange",
    label: "Created",
    isActive: (filters) => Boolean(filters.createdFrom || filters.createdTo),
    clearKeys: ["createdFrom", "createdTo"],
  },
  {
    key: "updatedRange",
    label: "Updated",
    isActive: (filters) => Boolean(filters.updatedFrom || filters.updatedTo),
    clearKeys: ["updatedFrom", "updatedTo"],
  },
  {
    key: "createdByBrokerAdminId",
    label: "Created by",
    isActive: (filters) => Boolean(filters.createdByBrokerAdminId),
  },
  {
    key: "brokerTags",
    label: "Tags",
    isActive: (filters) => Boolean(filters.brokerTags),
  },
];

const getActiveOptionalKeys = (filters, definitions = optionalFilterDefinitions) => definitions
  .filter((item) => item.isActive(filters || {}))
  .map((item) => item.key);

const FilterBar = ({
  ariaLabel = "Partner application filters",
  filters,
  onChange,
  onClear,
  onRefresh,
  refreshing = false,
  onNewApplication,
  primaryActionTitle = "New application",
  primaryActionAriaLabel = primaryActionTitle,
  PrimaryActionIcon = FaPlus,
  refreshActionTitle = "Search",
  refreshActionAriaLabel = refreshActionTitle,
  statusLabel = "Status",
  statusKey = "status",
  statusId = "broker-status-filter",
  statusTestId = "broker-applications-filter-status",
  statusPlaceholder = "All",
  statusOptions: fixedStatusOptions = statusOptions,
  optionalFilters = optionalFilterDefinitions,
  defaultOptionalKeys = EMPTY_OPTIONAL_KEYS,
  sortFieldOptions: configuredSortFieldOptions = sortFieldOptions,
  sortId = "broker-application-sort",
  sortTestId = "broker-applications-sort",
  showSort = true,
  sortValue,
  onSortChange,
  tagSuggestions = [],
  tagsLoading = false,
  systemTagOptions = [],
  systemTagsLoading = false,
}) => {
  const [sortField, sortDirection] = String(sortValue || "updatedAt:desc").split(":");
  const sortAsc = sortDirection === "asc";
  const [selectedOptionalKeys, setSelectedOptionalKeys] = useState(() => (
    optionalFilters
      .map((item) => item.key)
      .filter((key) => defaultOptionalKeys.includes(key) || getActiveOptionalKeys(filters, optionalFilters).includes(key))
  ));
  const [filterMenuOpen, setFilterMenuOpen] = useState(false);
  const [filterQuery, setFilterQuery] = useState("");
  const addFilterRef = useRef(null);

  // Draft filters — controls edit this locally; nothing hits the API until the
  // user presses Search (or Enter). Mirrors the submit-on-search UX in Booking.
  const [draft, setDraft] = useState(filters || {});
  useEffect(() => { setDraft(filters || {}); }, [filters]);

  const update = (key, value) => setDraft((prev) => ({ ...(prev || {}), [key]: value }));

  // Commit the draft to the parent, which triggers the API fetch.
  const applyFilters = () => {
    setFilterMenuOpen(false);
    onChange?.(draft);
  };
  const handleSearchKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      applyFilters();
    }
  };
  const searchInputProps = { onKeyDown: handleSearchKeyDown };

  useEffect(() => {
    setSelectedOptionalKeys((current) => {
      const currentSet = new Set(current);
      defaultOptionalKeys.forEach((key) => currentSet.add(key));
      getActiveOptionalKeys(filters, optionalFilters).forEach((key) => currentSet.add(key));
      return optionalFilters
        .map((item) => item.key)
        .filter((key) => currentSet.has(key));
    });
  }, [defaultOptionalKeys, filters, optionalFilters]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (addFilterRef.current && !addFilterRef.current.contains(event.target)) {
        setFilterMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectedOptionalFilters = useMemo(() => (
    optionalFilters.filter((item) => selectedOptionalKeys.includes(item.key))
  ), [optionalFilters, selectedOptionalKeys]);

  const availableFilters = useMemo(() => {
    const needle = filterQuery.trim().toLowerCase();
    return optionalFilters
      .filter((item) => !selectedOptionalKeys.includes(item.key))
      .filter((item) => !needle || item.label.toLowerCase().includes(needle));
  }, [filterQuery, optionalFilters, selectedOptionalKeys]);
  const hasAvailableOptionalFilters = useMemo(() => (
    optionalFilters.some((item) => !selectedOptionalKeys.includes(item.key))
  ), [optionalFilters, selectedOptionalKeys]);

  const addOptionalFilter = (key) => {
    setSelectedOptionalKeys((current) => current.includes(key) ? current : [...current, key]);
    setFilterQuery("");
    setFilterMenuOpen(false);
  };

  const removeOptionalFilter = (definition) => {
    const clearKeys = definition.clearKeys || [definition.key];
    // Drop the chip and clear its draft value; applied on the next Search.
    setDraft((prev) => ({
      ...(prev || {}),
      ...Object.fromEntries(clearKeys.map((key) => [key, ""])),
    }));
    setSelectedOptionalKeys((current) => current.filter((key) => key !== definition.key));
  };

  const renderOptionalControl = (definition) => {
    if (definition.control === "text") {
      const valueKey = definition.valueKey || definition.key;
      return (
        <InputBar
          id={definition.id || `broker-filter-${definition.key}`}
          testId={definition.testId || `broker-applications-filter-${definition.key}`}
          value={draft[valueKey] || ""}
          onChange={(value) => update(valueKey, value)}
          placeholder={definition.placeholder || "Search"}
          type={definition.type || "search"}
          inputProps={searchInputProps}
        />
      );
    }
    switch (definition.key) {
      case "view":
        return (
          <SelectDropdown
            id="broker-view-filter"
            testId="broker-applications-filter-view"
            options={viewOptions}
            value={draft.view || "all"}
            onChange={(value) => update("view", value || "all")}
            showClear={false}
            searchPlaceholder="Search views"
          />
        );
      case "createdRange":
        return (
          <div className="broker-filter-chip-range" aria-label="Created date range">
            <DateBar
              id="broker-created-from-filter"
              testId="broker-applications-filter-created-from"
              value={draft.createdFrom || ""}
              onChange={(value) => update("createdFrom", value)}
            />
            <DateBar
              id="broker-created-to-filter"
              testId="broker-applications-filter-created-to"
              value={draft.createdTo || ""}
              onChange={(value) => update("createdTo", value)}
            />
          </div>
        );
      case "kycStatus":
        return (
          <MultiSelectDropdown
            id="broker-kyc-filter"
            testId="broker-applications-filter-kyc"
            options={kycStatusOptions}
            value={toValueArray(draft.kycStatus)}
            onChange={(values) => update("kycStatus", toCsv(values))}
            placeholder="KYC status"
          />
        );
      case "type":
        return (
          <MultiSelectDropdown
            id="broker-type-filter"
            testId="broker-applications-filter-type"
            options={applicationTypeOptions}
            value={toValueArray(draft.type)}
            onChange={(values) => update("type", toCsv(values))}
            placeholder="Application type"
          />
        );
      case "systemTag":
        return (
          <MultiSelectDropdown
            id="broker-system-tag-filter"
            testId="broker-applications-filter-system-tag"
            options={systemTagOptions}
            value={toValueArray(draft.systemTag)}
            onChange={(values) => update("systemTag", toCsv(values))}
            placeholder="System tag"
            loading={systemTagsLoading}
          />
        );
      case "createdByBrokerAdminId":
        return (
          <InputBar
            id="broker-created-by-filter"
            testId="broker-applications-filter-created-by"
            value={draft.createdByBrokerAdminId || ""}
            onChange={(value) => update("createdByBrokerAdminId", value)}
            placeholder="Admin ID"
            inputProps={searchInputProps}
          />
        );
      case "brokerTags":
        return (
          <MultiSelectDropdown
            id="broker-tags-filter"
            testId="broker-applications-filter-tags"
            options={(tagSuggestions || []).map((tag) => ({
              value: tag.tag_id || tag.value,
              label: tag.details || tag.label || tag.tag_id || tag.value,
            }))}
            value={toValueArray(draft.brokerTags)}
            onChange={(values) => update("brokerTags", toCsv(values))}
            placeholder="Partner tags"
            loading={tagsLoading}
          />
        );
      case "search":
        return (
          <InputBar
            id={definition.id || "broker-search-filter"}
            testId={definition.testId || "broker-filter-search"}
            value={draft[definition.valueKey || definition.key] || ""}
            onChange={(value) => update(definition.valueKey || definition.key, value)}
            placeholder={definition.placeholder || "Search"}
            type="search"
            inputProps={searchInputProps}
          />
        );
      case "updatedRange":
        return (
          <div className="broker-filter-chip-range" aria-label="Updated date range">
            <DateBar
              id="broker-updated-from-filter"
              testId="broker-applications-filter-updated-from"
              value={draft.updatedFrom || ""}
              onChange={(value) => update("updatedFrom", value)}
            />
            <DateBar
              id="broker-updated-to-filter"
              testId="broker-applications-filter-updated-to"
              value={draft.updatedTo || ""}
              onChange={(value) => update("updatedTo", value)}
            />
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="broker-filterbar broker-filterbar--booking" role="search" aria-label={ariaLabel}>
      <div className="broker-bar">
        <div className="broker-bar__left">
          {/* Fixed, non-removable Status filter — inline input-group style */}
          <div className="broker-filter-chip-control broker-filter-chip-control--fixed broker-bar__status">
            <span className="broker-filter-chip-control__label">{statusLabel}</span>
            <div className="broker-filter-chip-control__field">
              <MultiSelectDropdown
                id={statusId}
                testId={statusTestId}
                options={fixedStatusOptions}
                value={toValueArray(draft[statusKey])}
                onChange={(values) => update(statusKey, toCsv(values))}
                placeholder={statusPlaceholder}
              />
            </div>
          </div>

          {/* Added filters — scrolls horizontally when it overflows */}
          {selectedOptionalFilters.length ? (
            <div className="broker-bar__chips" role="list">
              {selectedOptionalFilters.map((definition) => {
                const isDefaultFilter = defaultOptionalKeys.includes(definition.key);
                return (
                  <div className="broker-filter-chip-control" role="listitem" key={definition.key}>
                    <span className="broker-filter-chip-control__label">{definition.label}</span>
                    <div className="broker-filter-chip-control__field">
                      {renderOptionalControl(definition)}
                    </div>
                    {!isDefaultFilter ? (
                      <button
                        type="button"
                        className="broker-filter-chip-control__remove"
                        onClick={() => removeOptionalFilter(definition)}
                        aria-label={`Remove ${definition.label} filter`}
                      >
                        <FaTimes aria-hidden="true" />
                      </button>
                    ) : null}
                  </div>
                );
              })}
            </div>
          ) : null}

          {hasAvailableOptionalFilters ? (
            <div className="broker-add-filter" ref={addFilterRef}>
              <FaFilter className="broker-add-filter__icon" aria-hidden="true" />
              <input
                type="text"
                value={filterQuery}
                onChange={(event) => {
                  setFilterQuery(event.target.value);
                  setFilterMenuOpen(true);
                }}
                onFocus={() => setFilterMenuOpen(true)}
                // Picking an item keeps the input focused (menu uses mousedown +
                // preventDefault), so onFocus won't refire — reopen on click too.
                onClick={() => setFilterMenuOpen(true)}
                placeholder="Add filter"
                aria-label="Add filter"
              />
              {filterMenuOpen ? (
                <div className="broker-add-filter__menu" role="listbox">
                  {availableFilters.length ? availableFilters.map((definition) => (
                    <button
                      type="button"
                      key={definition.key}
                      onMouseDown={(event) => {
                        event.preventDefault();
                        addOptionalFilter(definition.key);
                      }}
                      role="option"
                      aria-selected="false"
                    >
                      {definition.label}
                    </button>
                  )) : (
                    <span>No filters available</span>
                  )}
                </div>
              ) : null}
            </div>
          ) : null}

          {/* Apply action — kept beside the filters so it reads as "apply these
              filters", not a generic search. */}
          <Button
            type="button"
            color="primary"
            className="broker-filterbar-action broker-filterbar-action--icon broker-filterbar-action--search"
            onClick={applyFilters}
            disabled={refreshing}
            aria-label={refreshActionAriaLabel}
            title={refreshActionTitle}
          >
            {refreshing ? <FaSpinner className="broker-spin" aria-hidden="true" /> : <FaSearch aria-hidden="true" />}
          </Button>
        </div>

        <div className="broker-bar__actions">
          {showSort ? (
            <div className="broker-sort">
              <div className="broker-booking-field broker-booking-field--sort">
                <SelectDropdown
                  id={sortId}
                  testId={sortTestId}
                  options={configuredSortFieldOptions}
                  value={sortField || "updatedAt"}
                  onChange={(value) => onSortChange?.(`${value || "updatedAt"}:${sortAsc ? "asc" : "desc"}`)}
                  showClear={false}
                  searchPlaceholder="Search sort fields"
                />
              </div>
              <button
                type="button"
                className="broker-filterbar-action broker-filterbar-action--icon broker-sort__dir"
                onClick={() => onSortChange?.(`${sortField || "updatedAt"}:${sortAsc ? "desc" : "asc"}`)}
                title={sortAsc ? "Ascending — click for descending" : "Descending — click for ascending"}
                aria-label="Toggle sort direction"
              >
                {sortAsc ? <FaSortAmountDownAlt aria-hidden="true" /> : <FaSortAmountDown aria-hidden="true" />}
              </button>
            </div>
          ) : null}
          {onNewApplication ? (
            <Button
              type="button"
              color="primary"
              className="broker-filterbar-action broker-filterbar-action--icon broker-filterbar-action--primary"
              onClick={onNewApplication}
              title={primaryActionTitle}
              aria-label={primaryActionAriaLabel}
            >
              <PrimaryActionIcon size={15} aria-hidden="true" />
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default FilterBar;
